/**
 * The agent binding.
 *
 * One rule, and it is the whole item: **the base URL is resolved on every
 * request, never captured.** The agent's port is ephemeral — a different one on
 * every launch, and a different one again if the agent dies and is respawned
 * mid-session (Corrections #15). 2.x could cache it because 8935 was forever:
 * `apiUtils.ts` holds `LOCAL_AGENT_BASE_URL` as a module-level `const`, which is
 * correct there and a guaranteed stale-port bug here.
 *
 * So there is no `baseUrl` export, no memo, and no module-level read of
 * `window.bimgo`. `createAgentClient` takes its probe as an argument, which is
 * what makes "resolved per request" something a test can observe rather than
 * something a comment claims.
 *
 * The other half is that *not having an agent* is a set of distinguishable
 * states, not one error. An ordinary browser, a desktop too old to report the
 * agent, an agent still starting and an agent that died all need different words
 * on screen — collapsing them into "request failed" is how a starting agent
 * comes to look like a broken one.
 */

import type { AgentState } from "../bimgo";

/**
 * What a look at the bridge found.
 *
 * `resolving` is the state before the first `agent.state()` resolves, and it is
 * deliberately distinct from `stopped` — the same distinction `AuthProvider`
 * draws between "not asked yet" and "signed out" (Corrections #26). Without it
 * the UI renders "agent unavailable" for a frame on every launch.
 */
export type BridgeProbe =
  | { kind: "resolving" }
  | { kind: "no-desktop" }
  | { kind: "no-agent-channel" }
  | { kind: "state"; state: AgentState };

export type AgentUnavailableReason =
  | "resolving"
  | "no-desktop"
  | "no-agent-channel"
  | "starting"
  | "failed"
  | "stopped";

export type AgentTarget =
  | { ok: true; baseUrl: string; port: number | null }
  | { ok: false; reason: AgentUnavailableReason; detail: string | null };

/**
 * Probe → target. Pure, so the mapping every screen depends on is testable
 * without a bridge, a window, or an agent.
 */
export function agentTarget(probe: BridgeProbe): AgentTarget {
  switch (probe.kind) {
    case "resolving":
      return { ok: false, reason: "resolving", detail: null };
    case "no-desktop":
      return { ok: false, reason: "no-desktop", detail: null };
    case "no-agent-channel":
      return { ok: false, reason: "no-agent-channel", detail: null };
    case "state": {
      const { status, baseUrl, port, error } = probe.state;
      if (status === "ready") {
        // A "ready" agent with no URL is not usable, whatever it says. Trusting
        // the status over the field it is supposed to accompany is how a
        // request ends up at `undefined/health`.
        if (!baseUrl) {
          return { ok: false, reason: "failed", detail: "agent reported ready with no base URL" };
        }
        return { ok: true, baseUrl, port: port ?? null };
      }
      return { ok: false, reason: status, detail: error };
    }
  }
}

/**
 * One sentence for each way the agent can be unavailable.
 *
 * **The healthy case names no address.** The port is ephemeral and changes on
 * every launch, so it is a number that moves in the corner of the screen and
 * tells a user nothing they can act on — while implying the app expects them to
 * care. It has not been deleted: it is in About, with the rest of the technical
 * detail, which is where somebody goes when they *do* need it.
 *
 * The failure sentences keep their detail, because there the specifics are the
 * whole message.
 */
export function describeAgent(target: AgentTarget): string {
  if (target.ok) return "agent ready";
  switch (target.reason) {
    case "resolving":
      return "checking for the local agent…";
    case "no-desktop":
      return "no desktop app — the local agent is not reachable from a browser";
    case "no-agent-channel":
      return "this desktop version does not report the agent";
    case "starting":
      return "starting the local agent…";
    case "stopped":
      return "the local agent is not running";
    case "failed":
      return target.detail ? `the local agent failed: ${target.detail}` : "the local agent failed";
  }
}

export class AgentUnavailableError extends Error {
  readonly reason: AgentUnavailableReason;
  readonly detail: string | null;

  constructor(reason: AgentUnavailableReason, detail: string | null) {
    super(detail ? `agent unavailable (${reason}): ${detail}` : `agent unavailable (${reason})`);
    this.name = "AgentUnavailableError";
    this.reason = reason;
    this.detail = detail;
  }
}

/**
 * Join without doubling or dropping the separator. Both halves vary: the base
 * URL comes from the bridge and the path from the caller, and `//health` and
 * `127.0.0.1:5000health` are both 404s that read as agent bugs.
 */
export function joinAgentUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
}

/**
 * Look at the bridge **now**.
 *
 * Every read of `window.bimgo` in this file is inside this function, and this
 * function is called per request. Hoisting any of it to module scope would
 * reintroduce exactly the bug the item exists to prevent, and would break in a
 * second way besides: the module may be evaluated before the desktop has
 * finished starting the agent.
 */
export async function probeBridge(): Promise<BridgeProbe> {
  const bimgo = window.bimgo;
  if (!bimgo) return { kind: "no-desktop" };
  // Feature-detect the exact call. `agent` arrived at bridge v2, so a desktop
  // older than this web build has `window.bimgo` and no `agent` at all — the
  // skew hard rule 6 exists for.
  if (!bimgo.agent?.state) return { kind: "no-agent-channel" };
  return { kind: "state", state: await bimgo.agent.state() };
}

export interface AgentClient {
  /** Resolves the current base URL, or throws `AgentUnavailableError`. */
  baseUrl(): Promise<string>;
  /** `fetch` against the agent, with the base URL resolved for this call. */
  fetch(path: string, init?: RequestInit): Promise<Response>;
}

export function createAgentClient(
  deps: {
    probe?: () => Promise<BridgeProbe>;
    fetchImpl?: typeof fetch;
  } = {},
): AgentClient {
  const probe = deps.probe ?? probeBridge;
  const fetchImpl = deps.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));

  async function baseUrl(): Promise<string> {
    const target = agentTarget(await probe());
    if (!target.ok) throw new AgentUnavailableError(target.reason, target.detail);
    return target.baseUrl;
  }

  return {
    baseUrl,
    async fetch(path: string, init?: RequestInit): Promise<Response> {
      // Resolved here, inside the call. Not in a closure, not in a field.
      const resolved = await baseUrl();
      return fetchImpl(joinAgentUrl(resolved, path), init);
    },
  };
}

/** The client the app uses. Wired to the real bridge; holds no state. */
export const agentClient: AgentClient = createAgentClient();
